import { FC, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { concatMap, of } from 'rxjs';
import { capitalCase } from 'change-case';
import { NumberUtils, RxUtils, Tr } from '@utils';
import { useService } from '@hooks';
import {
	notificationService,
	ProductCategoryType,
	productService,
	QuotationItem,
	quotationService,
	simulationService,
	siteService
} from '@services';
import { Autocomplete, Box, Grid, Stack, Typography } from '@mui/material';
import { EntityList, EntityListItem, FormikTextfield, VoltaageBadge } from '@components/common';
import { EmptyEntityPageLayout, PortalPageTitle } from '@components/portal/common';
import { LocationInfrastructureSizingKPIs } from '@components/portal/locations';
import { QuotationItemsTable } from '@components/portal/quotation';
import { ReactComponent as Illustration } from '@assets/illustrations/no-stock-added.svg';

const SEARCH_PARAM_SITE_KEY = 'site';
const SEARCH_PARAM_PARKAREA_KEY = 'park-area';
const paddingX = 9;
const PRODUCT_CATEGORIES: ProductCategoryType[] = [
	ProductCategoryType.CHARGING_POINT,
	ProductCategoryType.ACCESSORIES,
	ProductCategoryType.SERVICE
];

export const AllQuotation: FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation('portal');
	const [searchParams, setSearchParams] = useSearchParams();
	const [totalPrice, setTotalPrice] = useState(0);
	const [selectedSite, setSelectedSite] = useState<string>();
	const [selectedParkArea, setSelectedParkArea] = useState<string>();
	const { data: sites = [] } = useService(() => siteService.getAll());
	const { data: products = [] } = useService(() => productService.getAll());
	const { data: parkAreas = [] } = useService(() => (selectedSite ? siteService.parkArea(selectedSite).getAll() : of()), [selectedSite]);
	const { data: kpi } = useService(() => simulationService.getKPIs(selectedSite), [selectedSite]);
	const { data: latestQuotation } = useService(() => quotationService.getLatest());
	const { data: quotationItemsGrouped = {}, trigger: fetchQuotationItems } = useService(() => {
		if (latestQuotation?.id) {
			if (selectedParkArea) {
				return quotationService.quotationItem(latestQuotation.id).getItemsForParkingAreaGrouped(selectedParkArea);
			} else if (selectedSite) {
				return quotationService.quotationItem(latestQuotation.id).getItemsForSiteGrouped(selectedSite);
			} else {
				return quotationService.quotationItem(latestQuotation.id).getItemsGrouped();
			}
		} else {
			return of();
		}
	}, [latestQuotation, selectedSite, selectedParkArea]);

	const updateSearchParam = (key: string, value?: string) => {
		value ? searchParams.set(key, value) : searchParams.delete(key);
		setSearchParams(searchParams, { replace: true });
	};
	const onEdit = (id: string, data: Partial<QuotationItem>) => {
		selectedSite &&
			selectedParkArea &&
			RxUtils.promisify(
				quotationService.quotationItem(latestQuotation?.id || '').update(id, data),
				() => (fetchQuotationItems(), notificationService.sendSuccess(t('quotation.quotation-item-update-successfull')))
			);
	};
	const onAdd = (data: Partial<QuotationItem>) => {
		selectedSite &&
			selectedParkArea &&
			RxUtils.promisify(
				quotationService
					.quotationItem(latestQuotation?.id || '')
					.create({ ...data, numberOfItems: undefined, siteId: selectedSite, parkAreaId: selectedParkArea })
					.pipe(concatMap(({ id }) => quotationService.quotationItem(latestQuotation?.id || '').update(id, data))),
				() => (fetchQuotationItems(), notificationService.sendSuccess(t('quotation.quotation-item-update-successfull')))
			);
	};

	useEffect(() => {
		setTotalPrice(
			Object.values(quotationItemsGrouped)
				.flat()
				.reduce((acc, { totalPriceAfterDiscount, checkout }) => acc + (checkout ? totalPriceAfterDiscount : 0), 0)
		);
	}, [quotationItemsGrouped]);

	useEffect(() => {
		setSelectedSite(searchParams.get(SEARCH_PARAM_SITE_KEY) || undefined);
		setSelectedParkArea(searchParams.get(SEARCH_PARAM_PARKAREA_KEY) || undefined);
	}, [searchParams]);

	return (
		<Stack direction="column" spacing={4} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5, overflowY: 'auto' }}>
			<PortalPageTitle
				title={<Tr.Portal path="quotation.title" options={{ name: '' }} />}
				subtitle={<Tr.Portal path="quotation.subtitle" />}
				paddingX={3}
				onBackButtonClick={() => navigate(-1)}
			/>

			<Box sx={{ paddingX }}>
				<LocationInfrastructureSizingKPIs kpi={kpi} />
			</Box>

			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ paddingX, width: 1.0 }} spacing={2}>
				<Typography sx={{ minWidth: 'fit-content' }}>
					<Tr.Portal path="quotation.filter" />
				</Typography>
				<Grid container>
					<Grid item xs={4}>
						<Autocomplete
							isOptionEqualToValue={(option, value) => option.id === value.id}
							getOptionLabel={(option) => option?.name}
							options={sites}
							fullWidth
							autoHighlight
							onChange={(e, value) => (
								updateSearchParam(SEARCH_PARAM_SITE_KEY, value?.id), updateSearchParam(SEARCH_PARAM_PARKAREA_KEY, undefined)
							)}
							onInputChange={(_e, _value, reason) =>
								reason === 'clear' && updateSearchParam(SEARCH_PARAM_SITE_KEY, undefined)
							}
							inputValue={sites.find(({ id }) => id === selectedSite)?.name || ''}
							renderInput={(params) => (
								<FormikTextfield
									{...params}
									withoutFormik
									name="site"
									placeholder={t('quotation.select-location')}
									InputProps={{
										...params.InputProps,
										endAdornment: params.InputProps.endAdornment
									}}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={4} sx={{ marginLeft: 2 }}>
						<Autocomplete
							isOptionEqualToValue={(option, value) => option.id === value.id}
							getOptionLabel={(option) => option?.name}
							options={parkAreas}
							fullWidth
							autoHighlight
							onChange={(e, value) => updateSearchParam(SEARCH_PARAM_PARKAREA_KEY, value?.id)}
							onInputChange={(_e, _value, reason) =>
								reason === 'clear' && updateSearchParam(SEARCH_PARAM_PARKAREA_KEY, undefined)
							}
							inputValue={parkAreas.find(({ id }) => id === selectedParkArea)?.name || ''}
							disabled={!selectedSite}
							renderInput={(params) => (
								<FormikTextfield
									{...params}
									withoutFormik
									name="park-area"
									placeholder={t('quotation.select-park-area')}
									InputProps={{
										...params.InputProps,
										endAdornment: params.InputProps.endAdornment
									}}
								/>
							)}
						/>
					</Grid>
				</Grid>
				{Object.keys(quotationItemsGrouped).length ? (
					<Typography sx={{ fontWeight: 600, color: 'primary.main', minWidth: 'fit-content' }}>
						<Tr.Portal
							path="quotation.quotation-total-price"
							options={{ price: NumberUtils.formatAsPrice(totalPrice, { decimalScale: 0 }) }}
						/>
					</Typography>
				) : null}
			</Stack>

			{Object.keys(quotationItemsGrouped).length ? (
				<EntityList entities={[{}]} hideSelectAllButton paddingX={paddingX}>
					{PRODUCT_CATEGORIES.map((category, i) => {
						const items = quotationItemsGrouped[category] || [];
						const cost = items.reduce(
							(acc, { totalPriceAfterDiscount, checkout }) => acc + (checkout ? totalPriceAfterDiscount : 0),
							0
						);
						return (
							<EntityListItem
								key={`quotation-item-group-${category}-${i}`}
								sx={{ paddingRight: paddingX }}
								notSelectable
								accordionTrigger="icon-button"
								expandedInitially
								expandedContent={
									<QuotationItemsTable
										quotationItems={items}
										products={products.filter((product) => category === product.category)}
										notEditable={!(selectedSite && selectedParkArea)}
										onEdit={onEdit}
										onAdd={onAdd}
									/>
								}
							>
								<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: 1.0 }}>
									<Typography sx={{ fontWeight: 600 }}>
										<Tr.Portal
											path="quotation.category-type-title"
											options={{ productCategory: capitalCase(category) }}
										/>
									</Typography>
									<VoltaageBadge>
										<Typography sx={{ fontWeight: 600 }}>
											<Tr.Portal
												path="quotation.category-partial-price"
												options={{ price: NumberUtils.formatAsPrice(cost, { decimalScale: 0 }) }}
											/>
										</Typography>
									</VoltaageBadge>
								</Stack>
							</EntityListItem>
						);
					})}
				</EntityList>
			) : (
				<EmptyEntityPageLayout illustration={<Illustration />} title={<Tr.Portal path="quotation.no-entity-title" />} />
			)}
		</Stack>
	);
};
